import React, { useEffect, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import { productService } from "../../services/productService";
import { STOREFRONT_HOST } from "../../config/storefront";
import { SLUG_RE } from "../../utils/slug";

interface Props {
    slug: string;
    /** The product being edited (its own address counts as free). */
    productId?: string;
    /** The address it has now — no check needed while unchanged. */
    savedSlug?: string;
    /** For a new product left on the automatic address: a taken one gets a
     * number added rather than being refused. */
    automatic?: boolean;
    /** Offer the address made from the name, when it's different. */
    suggestion?: string;
    onUseSuggestion?: (slug: string) => void;
}

/**
 * Under a web-address field: the full address, whether it's free (checked
 * as you type), and what happens to the old address when it changes.
 */
export const ProductSlugHint: React.FC<Props> = ({
    slug,
    productId,
    savedSlug,
    automatic = false,
    suggestion,
    onUseSuggestion,
}) => {
    const [state, setState] = useState<"idle" | "checking" | "free" | "taken" | "invalid">("idle");
    const [reason, setReason] = useState<string | null>(null);
    const unchanged = !!savedSlug && slug === savedSlug;

    useEffect(() => {
        if (!slug || unchanged) {
            setState("idle");
            return;
        }
        if (!SLUG_RE.test(slug)) {
            setState("invalid");
            return;
        }
        setState("checking");
        let live = true;
        const timer = setTimeout(() => {
            productService
                .checkProductSlug(slug, productId)
                .then((res) => {
                    if (!live) return;
                    setState(res.available ? "free" : "taken");
                    setReason(res.reason);
                })
                .catch(() => live && setState("idle"));
        }, 400);
        return () => {
            live = false;
            clearTimeout(timer);
        };
    }, [slug, productId, unchanged]);

    const status =
        state === "free" ? (
            <Box component="span" sx={{ color: "success.main", display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                <CheckCircleOutline sx={{ fontSize: 14 }} /> Available
            </Box>
        ) : state === "taken" ? (
            <Box
                component="span"
                sx={{ color: automatic ? "text.secondary" : "error.main", display: "inline-flex", alignItems: "center", gap: 0.25 }}
            >
                <ErrorOutline sx={{ fontSize: 14 }} />
                {automatic ? "Already used — a number will be added (…-2)" : reason || "Already used by another product"}
            </Box>
        ) : state === "invalid" ? (
            <Box component="span" sx={{ color: "error.main" }}>
                Use lowercase letters, numbers and single hyphens
            </Box>
        ) : state === "checking" ? (
            <Box component="span" sx={{ color: "text.secondary" }}>
                Checking…
            </Box>
        ) : null;

    return (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, display: "grid", gap: 0.25 }}>
            <Box component="span" sx={{ wordBreak: "break-all" }}>
                {STOREFRONT_HOST}/products/{slug || "…"}
            </Box>
            {status}
            {savedSlug && slug && !unchanged && (
                <Box component="span">The old address keeps working and forwards here.</Box>
            )}
            {suggestion && suggestion !== slug && onUseSuggestion && (
                <Box component="span">
                    From the name:{" "}
                    <Link component="button" type="button" variant="caption" onClick={() => onUseSuggestion(suggestion)}>
                        {suggestion}
                    </Link>
                </Box>
            )}
        </Typography>
    );
};

export default ProductSlugHint;
